import React from 'react';
import { useTranslation } from 'react-i18next';
import { reportConversion } from '../utils/gtag';

// A slim bar displayed below the main header with a WhatsApp booking CTA.
// Appears on all pages when included in the layout.
export default function TopWhatsAppBar() {
  const { t } = useTranslation();
  const whatsappUrl1 = 'https://wa.me/971568699800';
  const whatsappUrl2 = 'https://wa.me/971524333690';
  const phoneHref1 = 'tel:+971568699800';
  const phoneHref2 = 'tel:+971524333690';
  const mapsHref = 'https://maps.app.goo.gl/TUp25rLZUxxKZjAn8';
  return (
    <div className="w-full sticky [top:var(--header-h,72px)] z-10 bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-sm">
        {/* Phone Numbers - Stack on mobile, row on desktop */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center sm:justify-start">
          <a
            href={phoneHref1}
            onClick={() => reportConversion(phoneHref1)}
            className="inline-flex items-center gap-1 text-white/90 hover:text-white transition font-semibold"
            aria-label="Call us at +971 56 8699 800"
          >
            <PhoneIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="tabular-nums text-[11px] sm:text-sm" dir="ltr">+971 56 8699 800</span>
          </a>
          <span className="hidden sm:inline opacity-40">|</span>
          <a
            href={phoneHref2}
            onClick={() => reportConversion(phoneHref2)}
            className="inline-flex items-center gap-1 text-white/90 hover:text-white transition font-semibold"
            aria-label="Call us at +971 52 433 3690"
          >
            <PhoneIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="tabular-nums text-[11px] sm:text-sm" dir="ltr">+971 52 433 3690</span>
          </a>
        </div>
        
        {/* WhatsApp & Location Buttons */}
        <div className="flex items-center gap-2 justify-center flex-wrap">
          <a
            href={whatsappUrl1}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Book Appointment on WhatsApp"
            onClick={() => reportConversion(whatsappUrl1)}
            className="inline-flex items-center gap-1.5 rounded-md bg-white text-emerald-700 font-semibold px-2.5 py-1.5 sm:px-3 sm:py-2 shadow hover:shadow-md hover:scale-[1.02] active:scale-[0.97] transition text-[10px] sm:text-xs"
          >
            <WhatsAppIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">{t('nav.whatsapp')}</span>
            <span className="xs:hidden">WA</span>
          </a>
          <a
            href={whatsappUrl2}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Book Appointment on WhatsApp (Alt)"
            onClick={() => reportConversion(whatsappUrl2)}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/90 text-emerald-700 font-semibold px-2.5 py-1.5 sm:px-3 sm:py-2 shadow hover:shadow-md hover:scale-[1.02] active:scale-[0.97] transition text-[10px] sm:text-xs"
          >
            <WhatsAppIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">{t('nav.whatsapp')}</span>
            <span className="xs:hidden">WA</span>
          </a>
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Ras Al Khor location in Google Maps"
            className="inline-flex items-center gap-2 rounded-md bg-white/10 text-white font-medium px-3 py-2 border border-white/20 hover:bg-white/20 transition text-xs sm:text-sm"
          >
            <LocationIcon className="w-4 h-4" />
            <span>{t('nav.rasAlKhorShort')}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function WhatsAppIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M16.04 3C9.4 3 4 8.4 4 15.06c0 2.64.92 5.08 2.46 7.04L4.67 29l7.17-1.75a12 12 0 0 0 4.2.74h.01c6.64 0 12.04-5.4 12.04-12.06A12 12 0 0 0 16.04 3Zm0 21.9c-1.3 0-2.58-.34-3.7-.98l-.27-.16-4.25 1.04 1.14-4.14-.18-.28a8.88 8.88 0 0 1-1.38-4.78c0-4.93 4.01-8.95 8.94-8.95 2.39 0 4.64.93 6.33 2.63a8.9 8.9 0 0 1 2.62 6.33c-.01 4.93-4.03 8.94-8.95 8.95Zm5.06-6.67c-.27-.13-1.6-.79-1.85-.88-.25-.1-.43-.13-.6.12-.18.26-.7.87-.86 1.05-.16.17-.32.2-.6.07-.27-.14-1.12-.41-2.13-1.31-.79-.7-1.32-1.57-1.48-1.84-.15-.26-.02-.4.11-.53.12-.12.27-.32.4-.48.13-.16.17-.26.26-.44.09-.18.05-.33-.02-.46-.07-.13-.6-1.44-.82-1.97-.22-.52-.44-.45-.6-.46l-.51-.01c-.18 0-.46.07-.7.33-.24.26-.92.9-.92 2.2 0 1.3.94 2.56 1.07 2.74.13.17 1.84 2.83 4.52 3.85.63.27 1.12.43 1.5.55.63.2 1.2.17 1.65.1.5-.07 1.6-.65 1.83-1.28.23-.63.23-1.16.16-1.28-.06-.11-.24-.18-.51-.3Z" />
    </svg>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function LocationIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
